/**
 * SPRINT 4J: Fractional Weight Selector
 * Allows buyers to select fractional weights (1/8, 1/4, 1/2, 3/4, 1+ kg)
 */

import { useState } from 'react';
import { Minus, Plus } from 'lucide-react';

interface WeightSelectorProps {
  pricePerKg: number;
  initialWeight?: number;
  onChange: (weight: number) => void;
  min?: number;
  max?: number;
}

const PRESET_WEIGHTS = [
  { label: '1/8 kg', value: 0.125 },
  { label: '1/4 kg', value: 0.25 },
  { label: '1/2 kg', value: 0.5 },
  { label: '3/4 kg', value: 0.75 },
  { label: '1 kg', value: 1 },
  { label: '2 kg', value: 2 },
  { label: '3 kg', value: 3 },
  { label: '5 kg', value: 5 },
];

export function WeightSelector({
  pricePerKg,
  initialWeight = 1,
  onChange,
  min = 0.125,
  max = 50,
}: WeightSelectorProps) {
  const [weight, setWeight] = useState(initialWeight);
  const [isCustomMode, setIsCustomMode] = useState(false);

  const handlePresetClick = (value: number) => {
    setWeight(value);
    onChange(value);
    setIsCustomMode(false);
  };

  const handleIncrement = () => {
    const newWeight = Math.min(weight + 0.125, max);
    setWeight(newWeight);
    onChange(newWeight);
  };

  const handleDecrement = () => {
    const newWeight = Math.max(weight - 0.125, min);
    setWeight(newWeight);
    onChange(newWeight);
  };

  const handleCustomInput = (value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= min && numValue <= max) {
      setWeight(numValue);
      onChange(numValue);
    }
  };

  const totalPrice = weight * pricePerKg;

  return (
    <div className="space-y-4">
      {/* Preset Weight Buttons */}
      {!isCustomMode && (
        <div className="grid grid-cols-4 gap-2">
          {PRESET_WEIGHTS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handlePresetClick(preset.value)}
              className={`py-2 px-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                weight === preset.value
                  ? 'border-green-600 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-green-500'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      {/* Custom Weight Input */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={handleDecrement}
          disabled={weight <= min}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-gray-300 hover:border-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>

        <div className="flex-1 text-center">
          {isCustomMode ? (
            <input
              type="number"
              value={weight}
              onChange={(e) => handleCustomInput(e.target.value)}
              onBlur={() => setIsCustomMode(false)}
              step={0.125}
              min={min}
              max={max}
              className="w-full text-center text-xl font-bold py-2 border-2 border-green-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsCustomMode(true)}
              className="text-xl font-bold py-2 hover:text-green-600"
            >
              {weight.toFixed(3).replace(/\.?0+$/, '')} kg
            </button>
          )}
        </div>

        <button
          onClick={handleIncrement}
          disabled={weight >= max}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-gray-300 hover:border-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Price Display */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Price per kg:</span>
          <span className="font-medium">₱{pricePerKg.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Weight:</span>
          <span className="font-medium">{weight.toFixed(3).replace(/\.?0+$/, '')} kg</span>
        </div>
        <div className="border-t pt-2 flex justify-between">
          <span className="font-bold text-gray-900">Total:</span>
          <span className="font-bold text-green-600 text-lg">
            ₱{totalPrice.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Helpful Text */}
      <p className="text-xs text-gray-500 text-center">
        Minimum: {min} kg • Maximum: {max} kg
      </p>
    </div>
  );
}

/**
 * Simple Quantity Selector for non-weight items
 */
interface QuantitySelectorProps {
  quantity: number;
  onChange: (quantity: number) => void;
  min?: number;
  max?: number;
}

export function QuantitySelector({
  quantity,
  onChange,
  min = 1,
  max = 100,
}: QuantitySelectorProps) {
  const handleIncrement = () => {
    if (quantity < max) {
      onChange(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > min) {
      onChange(quantity - 1);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={handleDecrement}
        disabled={quantity <= min}
        className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-gray-300 hover:border-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Minus className="w-4 h-4" />
      </button>

      <span className="text-lg font-bold w-12 text-center">{quantity}</span>

      <button
        onClick={handleIncrement}
        disabled={quantity >= max}
        className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-gray-300 hover:border-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}







